<template>
	<div class="d-inline-flex align-items-center">
		<nk-drop-button :options="dropButtonSettings" @click="buttonClicked"></nk-drop-button>
	</div>
</template>
<script>
	import dialog from '~/plugins/nikan-dialog-plugin'
	import AccessControl from '~/views/operation-roles/access-control'
	import { post } from '~/plugins/nikan-api'
	import Endpoints from '@nikan/endpoints'
	import { mapActions, mapGetters } from 'vuex'
	import $sc from '~/plugins/shortcut.collection.js'
	import emptyId from '~/statics/empty-id'
	import { i18nInstance } from '~/plugins/localization'
	import _ from 'lodash'
	import { stimulsoftCompatibility } from '~/utils/stimulsoft-compatibility'

	export default {
		props: ['options'],
		emits: ['buttonsChanged'],
		data() {
			return {
				settings: {
					disabled: false,
					isBulk: false
				},
				dropButtonSettings: {},
				oldForm: null,
				data: [],
				defaultPrint: null
			}
		},
		watch: {
			options: {
				immediate: true,
				deep: true,
				handler(val, oldVal) {
					this.settings = _.merge(this.settings, val)
					this.setDropButtonSettings()
					if (!this.oldForm && val || (this.oldForm && val && val.form !== this.oldForm)) {
						this.oldForm = val.form
						this.refreshItems(false)
					}
				}
			}
		},
		computed: {
			...mapGetters({
				getActiveState: 'appStates/getActiveByKey',
			}),
		},
		mounted() {
			this.options.printDefaultDesign = this.printDefault
		},
		methods: {
			...mapActions({
				getByForm: 'clientprint/getByForm',
				getPrintById: 'clientprint/getPrintById',
				deletePrintItem: 'clientprint/deletePrintItem',
				modifyStates: 'appStates/modify',
				syncStates: 'appStates/sync',
				refreshStates: 'appStates/refresh',
			}),
			setDropButtonSettings() {
				let item = {
					title: i18nInstance.global.t('print'),
					shortcut: $sc.find('print'),
					icon: 'print',
					isPrimary: false,
					disabled: !this.hasAccess('viewer') && !this.hasAccess('designer'),
					handle: async (button, page, e) => {
						this.printDefault(false, false)
					},
					selfButton: true,
					items: [],
					hideTitle: this.settings.hideTitle !== undefined ? this.settings.hideTitle : false,
					hideTooltip: this.settings.hideTooltip !== undefined ? this.settings.hideTooltip : true,
					tooltipEnabled: this.settings.tooltipEnabled !== undefined ? this.settings.tooltipEnabled : false,
					hideShortcut: true,
					...this.settings.buttonOptions
				}
				for (var i = 0; i < this.data.length; i++) {
					let designJson = this.data[i]
					let id = this.data[i].id
					let name = this.data[i].name

					item.items.push({
						title: name,
						icon: 'print',
						isPrimary: false,
						visible: this.hasAccess('viewer'),
						handle: async (button, page, e) => {
							let preHandlerResult = true
							if (this.settings.preHandler)
								preHandlerResult = this.settings.preHandler()
							if (!preHandlerResult) return
							if (this.settings.disabled) {
								this.$toast(i18nInstance.global.t('clientprint.m.load-data-first'), { type: 'error' })
								return
							}
							else if (typeof this.settings.isDataChanged === 'function' && await this.settings.isDataChanged()) {
								this.$toast(i18nInstance.global.t('clientprint.m.data-is-changed'), { type: 'info' })
								return
							}
							// this.saveDefaultPrint(id)
							this.showStimulModal('viewer', designJson)
						},
						commands: [
							{
								title: i18nInstance.global.t('clientprint.default'),
								icon: `fas fa-check-circle ${this.data[i].isDefault ? 'green' : 'disable'}`,
								handler: async () => {
									this.setDefaultPrint(designJson)
								},
							},
							{
								title: i18nInstance.global.t('edit'),
								icon: 'fas fa-edit orange',
								handler: async () => {
									if (typeof this.settings.isDataChanged === 'function' && await this.settings.isDataChanged()) {
										this.$toast(i18nInstance.global.t('clientprint.m.data-is-changed'), { type: 'info' })
										return
									}
									this.editDesign(designJson)
								},
								disabled: !this.data[i].canEdit || this.settings.disabled || !this.hasAccess('designer')
							},
							{
								title: i18nInstance.global.t('copy'),
								icon: 'fas fa-copy green',
								handler: () => {
									this.copyDesign(designJson)
								},
								// disabled: !this.data[i].canCopy
							},
							{
								title: i18nInstance.global.t('operationRole.claims'),
								icon: 'fas fa-user-lock darkred',
								handler: () => {
									this.accessManager(name, id)
								},
								disabled: !this.$nikan.checkPermission('General_Access_Management')
							},
							{
								title: i18nInstance.global.t('delete'),
								icon: 'fas fa-times red',
								handler: () => {
									this.deleteDesign(id)
								},
								disabled: !this.data[i].canDelete
							}
						]
					})
				}
				item.items.push({
					type: 'divider'
				})
				item.items.push({
					title: i18nInstance.global.t('new-print'),
					icon: 'plus',
					isPrimary: false,
					disabled: this.settings.disabled === true,
					visible: this.hasAccess('designer'),
					handle: async (button, page, e) => {
						if (typeof this.settings.isDataChanged === 'function' && await this.settings.isDataChanged()) {
							this.$toast(i18nInstance.global.t('clientprint.m.data-is-changed'), { type: 'info' })
							return
						}
						let preHandlerResult = true
						if (this.settings.preHandler)
							preHandlerResult = this.settings.preHandler()
						preHandlerResult && this.showStimulModal('designer')
					}
				})
				this.dropButtonSettings = item
			},

			hasAccess(target) {
				var hasAccess = false

				// Checking global print access
				if (this.$nikan.checkPermission(`General_Print_Print${target.upperCaseFirstLetter()}`))
					hasAccess = true

				// Checking form print access eg: Accounting_Document_PrintDesigner / Accounting_Document_ListPrintDesigner
				else if (this.$nikan.checkPermission(`${this.settings.claimPrefix}Print${target.upperCaseFirstLetter()}`))
					hasAccess = true

				return hasAccess

			},
			async showStimulModal(mode, designJson) {
				const wait = this.$wait(this.$el, { size: 'small' })

				const onSave = (res, isSaveAs) => {
					const design = { ...designJson }
					design.name = res.fileName.split('.')[0] // removing file extention from name
					res.report.reportFile = res.fileName
					design.options = res.report.saveToJsonString()
					design.id = isSaveAs ? emptyId : design.id || emptyId
					this.saveDesign(design)
				}

				try {
					const [{ default: StimulComponent }, printDesignRes] = await Promise.all([
						import('~/components/stimul/stimul.vue'),
						(designJson && !designJson.options)
							? this.getPrintById({ form: this.settings.form, printId: designJson.id })
							: Promise.resolve(null)
					])

					if (printDesignRes) {
						designJson.options = this.prepareReportOptions(printDesignRes.options)
					} else if (designJson && designJson.options) {
						designJson.options = this.prepareReportOptions(designJson.options)
					}

					wait && wait.end()

					return this.showModal({
						component: StimulComponent,
						options: {
							mode,
							reportObject: designJson?.options,
							getDataSources: this.settings.getDataSources,
							isBulk: this.settings.isBulk,
							additionalData: {
								form: this.settings.form,
								navMenuKey: this.settings.navMenuKey,
							},
							eventHandlers: {
								onSaveReport: (res) => onSave(res, false),
								onSaveAsReport: (res) => onSave(res, true),
								...this.settings.eventHandlers
							}
						}
					})
				} catch (err) {
					wait && wait.end()
				}
			},
			async refreshItems(force) {

				this.data = await this.getByForm({ form: this.settings.form, force: force })
				this.data.sort2('name')
				this.setDropButtonSettings()
			},
			buttonClicked(button, e) {
				e.preventDefault()
				var handle = button.handle
				if (handle != null) {
					handle(button, null, e)
				}
			},
			buttonOptionsChanged(button) {
				const index = this.buttons.findIndex(x => x.id === button.id)
				if (index > -1) {
					_.extend(this.buttons[index], button)
					this.$emit('buttonsChanged', this.id, this.buttons)
				}
			},
			prepareReportOptions(options) {
				if (!options) return options
				let printOptions = this.fixTypoIssues(options)
				return stimulsoftCompatibility.apply(printOptions)
			},
			fixTypoIssues(options) {
				let printOptions = options.replaceAll('&gt;', '>').replaceAll('&amp;', '&')
				return printOptions
			},
			preventDuplicateNames(designJson) {

				const regex = /.*?\s\((\d*)\)/g
				var baseName = designJson.name

				const findMaxIndice = (str) => {
					let types = []
					let curMatch

					while ((curMatch = regex.exec(str))) {
						types.push(parseInt(curMatch[1]))
					}

					if (!types.length)
						return 0

					return Math.max(...types)
				}

				if (designJson.name.search(regex) > -1) {
					var indice = findMaxIndice(baseName)
					baseName = baseName.replace(` (${indice})`, '')
				}

				var duplicateNames = this.data.filter(x => x.name.includes(baseName)).map(x => x.name).join() // creating string of duplicate names

				if (!duplicateNames.length)
					designJson.name = baseName

				else {
					var newIndice = findMaxIndice(duplicateNames) + 1
					designJson.name = `${baseName} (${newIndice})`
				}

				return designJson

			},
			editDesign(designJson) {
				this.showStimulModal('designer', designJson)
			},
			async copyDesign(designJson) {
				designJson = this.preventDuplicateNames(designJson)
				if (designJson && !designJson.options) {
					const wait = this.$wait(this.$el)
					const getPrintDesign = await this.getPrintById({ form: this.settings.form, printId: designJson.id })
					wait.end()
					if (getPrintDesign)
						designJson.options = this.prepareReportOptions(getPrintDesign.options)
				} else if (designJson && designJson.options) {
					designJson.options = this.prepareReportOptions(designJson.options)
				}
				designJson.id = emptyId
				designJson.isDefault = false
				this.saveDesign(designJson)
			},
			deleteDesign(id) {
				dialog({
					title: i18nInstance.global.t('clientprint.m.delete.title'),
					body: i18nInstance.global.t('clientprint.m.delete.want-to-delete'),
					type: 'warning',
					autoClose: true,
					buttons: [
						{
							title: i18nInstance.global.t('dialog.yes'),
							shortcut: $sc.find('yes', 'dialog'),
							isPrimary: true,
							handle: (button, page, e) => {
								var wait = this.$wait(this.grandParent)
								post(Endpoints.getUrl('print.delete'), { printId: id })
									.then(result => {
										wait.end()
										this.deletePrintItem({ form: this.settings.form, printId: id })
										this.refreshItems(false)
										this.$toast(i18nInstance.global.t('clientprint.m.delete.success-body'), { type: 'success' })
									})
									.catch(() => {
										wait.end()
									})
							}
						},
						{
							title: i18nInstance.global.t('dialog.cancel'),
							shortcut: $sc.find('cancel', 'dialog'),
							isCancel: true,
							handle: (button, page, e) => { }
						}
					]
				})
			},
			saveDesign(designJson) {
				var wait = this.$wait(this.grandParent)
				var id = designJson.id
				let url = id == emptyId ? Endpoints.getUrl('print.create') : Endpoints.getUrl('print.update')

				return new Promise((resolve, reject) => {

					if (id === emptyId)
						designJson = this.preventDuplicateNames(designJson)

					let data = {
						id: id,
						options: designJson.options,
						name: designJson.name,
						form: this.settings.form,
						isDefault: designJson.isDefault
					}

					post(url, { print: data })
						.then(result => {

							if (result.data)
								designJson.id = result.data.printId

							this.refreshItems(true)
							this.$toast(i18nInstance.global.t('clientprint.m.save.success-body'), { type: 'success' })
							wait.end()
							resolve(result)
						})
						.catch((err) => {
							reject(err)
							wait.end()
						})


				})
			},
			accessManager(name, id) {
				this.showModal({
					component: AccessControl,
					data: {
						prefix: 'Print',
						accessData: { entityId: id, entityType: 'Print', objectDescription: name }
					}
				}).then(node => {
				}).catch(() => {
				})
			},
			printDefault(skipLoadDataCheck = true, skipDataChangeCheck = false) {
				return new Promise(async (resolve, reject) => {
					let preHandlerResult = true
					if (this.settings.preHandler)
						preHandlerResult = this.settings.preHandler()
					if (!preHandlerResult) return

					let defaultPrint = this.data.find(item => item.isDefault)

					if (this.settings.disabled && !skipLoadDataCheck) {
						this.$toast(i18nInstance.global.t('clientprint.m.load-data-first'), { type: 'error' })
						reject()
						return
					}
					else if (!skipDataChangeCheck && (typeof this.settings.isDataChanged === 'function' && await this.settings.isDataChanged())) {
						this.$toast(i18nInstance.global.t('clientprint.m.data-is-changed'), { type: 'info' })
						return
					}
					if (!defaultPrint) {
						this.$toast(i18nInstance.global.t('clientprint.m.default-print-not-found'), { type: 'error' })
						reject()
						return
					}

					return this.showStimulModal('viewer', defaultPrint).then(() => {
						resolve()
					}).catch(() => {
						reject()
					})
				})
			},
			setDefaultPrint(design) {
				var wait = this.$wait(this.grandParent)

				post(Endpoints.getUrl('print.setDefault'), { id: design.id, isDefault: !design.isDefault }).then((res) => {
					this.$toast(i18nInstance.global.t('clientprint.m.set-default-success'), { type: 'success' })
					this.refreshItems(true)
				}).catch(() => { }).finally(() => {
					wait.end()
				})
			},
		}
	}
</script>